import type { Plugin } from '@capacitor/core';

/**
 * Represents a Garmin device.
 * This is similar to a native IQDevice object, providing basic details about the device.
 */
export interface IQDevice {
  /** Unique identifier for the device. */
  uuid: string;
  /** Model name of the Garmin device. Only on iOS */
  modelName?: string;
  /** Friendly name (often the device's display name). */
  friendlyName: string;
  /** Status of the device (e.g., 'connected', 'disconnected'). Optional field. */
  status?: string;
  /** Our boolean to check if the device is connected */
  isConnected?: boolean;
}

/**
 * Represents a Garmin app.
 * This is similar to a native IQApp object, providing details about the app.
 */
export interface IQApp {
  /** Unique identifier for the app. */
  uuid: string;
  /** Store identifier for the app (specific to the app's store). */
  storeUUID: string;
}

/**
 * Result of checking the application's installation status.
 * Used to check if the specified Garmin app is installed on the device.
 */
export interface CheckApplicationInfoResult {
  /** Status of the app: 'installed' if the app is installed, 'not_installed' otherwise. */
  installed: boolean;
  // if installed, the current version of the app
  version?: number;
}

export enum IQDeviceStatus {
  //No device with this UUID has been registered for status events
  invalidDevice = 'invalidDevice',
  // Bluetooth is either powered off or resetting.
  bluetoothNotReady = 'bluetoothNotReady',
  // This device could not be found by iOS. Perhaps the user removed the device?
  notFound = 'notFound',
  // The device is recognized by iOS, but it is not currently connected.
  notConnected = 'notConnected',
  // The device is connected and ready to communicate.
  connected = 'connected',

  // Android types are different from iOS
  //   NOT_PAIRED,
  //   NOT_CONNECTED,
  //   CONNECTED,
  //   UNKNOWN;
}

/**
 * Response after sending a message to the Garmin watch.
 * Indicates whether the message was successfully sent and provides a message.
 */
export interface SendMessageResponse {
  /** The status of the sent message (e.g., 'success', 'failure'). */
  status: string;
  /** A message providing details about the result of sending the message. */
  message: string;
}

export interface GarminDeviceStatus {
  isConnected: boolean;
  status: string;
}

export interface GarminDeviceStatusChangeEvent extends GarminDeviceStatus {
  uuid: string;
  friendlyName: string;
  isConnected: boolean;
  status: string;
}

/**
 * Garmin SDK Plugin interface that defines the available plugin methods for interacting
 * with Garmin devices and apps. The plugin provides functionalities for device management,
 * app installation status checking, and message communication.
 */
export interface GarminSDKPlugin extends Plugin {
  /**
   * Checks if the Garmin Connect app is installed on the device. if true
   * then it is safe to initialise the Garmin SDK.
   */
  checkGarminConnectInstalled(): Promise<{ installed: boolean }>;

  /**
   * Checks if the Garmin IQ store app is installed on the device, which
   * is required to install the Refsix app on the watch.
   */
  checkGarminConnectIQInstalled(): Promise<{ installed: boolean }>;

  /**
   * Open the gamrin connect mobile app on the user's phone if it is installed.
   * If not installed, the promise will rehect
   */
  openGarminConnect(): Promise<void>;

  /**
   * Start the garmin mobile sdk. This will trigger a notification for the
   * user to download the Garmin Connect app if it is not already installed. So
   * it is recommended to call this method only when the user expects it.
   */
  initialiseGarminSdk(): Promise<void>;

  /**
   * iOS Only, prompt the user to install Garmin Connect from the App Store.
   */
  openAppStoreForConnectMobile(): Promise<void>;

  /**
   * Retrieves all known Garmin devices, including those that are not currently connected.
   * Android: Does this silently behind the scenes.
   * iOS: This will take the use into the Garmin Connect app before returning to
   * the app with the list of devices encoded in the launch URL.
   * @returns A promise that resolves with a list of known devices.
   */
  getKnownDevices(): Promise<{ devices: IQDevice[] }>;

  /**
   * Android Only: Retrieves all currently connected Garmin devices.
   * @returns A promise that resolves with a list of currently connected devices.
   */
  getConnectedDevices(): Promise<{ devices: IQDevice[] }>;

  /**
   * Checks the installation status and version of a specified Garmin app.
   * Optionally allows comparing the app version with the current version.
   * @param params - Parameters including the device, app, and optional current version.
   * @returns A promise with the installation status and message.
   */
  checkApplicationInfo(params: { device: IQDevice; app: IQApp }): Promise<CheckApplicationInfoResult>;

  /**
   * Sends a message payload to a specified Garmin device and app.
   * @param params - Parameters including the device, app, and message payload.
   * @returns A promise with the status and message of the send operation.
   */
  sendMessageToWatch(params: {
    device: IQDevice;
    app: IQApp;
    payload: Record<string, unknown>; // Payload is a generic object, typically structured for the message.
  }): Promise<SendMessageResponse>;

  /**
   * Start listening for device status changes.
   * @param params - Parameters including the device to register for events.
   * @returns A promise indicating the event registration was successful.
   */
  registerForDeviceEvents(params: { device: IQDevice }): Promise<{ sent: true }>;

  /**
   * Unregisters from device-specific events.
   * @param params - Parameters including the device to unregister from events.
   * @returns A promise indicating the event unregistration was successful.
   */
  unregisterForDeviceEvents(params: { device: IQDevice }): Promise<{ sent: true }>;

  /**
   * Retrieves the current status of a specified Garmin device.
   * @param params - Parameters including the device to check the status of.
   * @returns A promise with the current status of the device.
   */
  getDeviceStatus(params: { device: IQDevice }): Promise<GarminDeviceStatus>;

  /**
   * Starts listening to messages from the watch.
   * @param params - Parameters including the device and app to start listening to.
   */
  startListeningToDevice(params: { device: IQDevice; app: IQApp }): void;

  /**
   * Stops listening to events related to a specific Garmin device and app.
   * @param params - Parameters including the device and app to stop listening to.
   * @returns A promise indicating that the stop listening operation was successful.
   */
  stopListeningToDevice(params: { device: IQDevice; app: IQApp }): Promise<{ sent: true }>;

  /**
   * Opens the Connect IQ store for a specified app on a Garmin device.
   * @param params - Parameters including the device and app to open the store for.
   * @returns A promise with the status of the operation.
   */
  showConnectIQStoreForApp(params: { device: IQDevice; app: IQApp }): Promise<{ status: string }>;

  /**
   * Opens a specified Garmin app on the device.
   * @param params - Parameters including the device and app to open.
   * @returns A promise with the status and message of the operation.
   */
  openGarminApp(params: { device: IQDevice; app: IQApp }): Promise<{ status: string; message: string }>;
}
